import React, { useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useGSAP } from '@/composables/useGSAP'

export type UITitleProps = React.HTMLProps<HTMLHeadingElement> & {
  is: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  i18nKey: string
  delay?: number
  hasScrollTrigger?: boolean
  hasSideDecoration?: boolean
  appear?: boolean
  animate?: boolean
}

export default function UITitle({
  is,
  i18nKey,
  className,
  delay = 0.5,
  hasScrollTrigger = false,
  hasSideDecoration = true,
  appear = true,
  animate = true,
}: UITitleProps) {
  const { gsap } = useGSAP()
  const { t } = useTranslation()

  const translatedText = t(i18nKey)
  const Component = is

  const elementRef = useRef<HTMLHeadingElement>(null)

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (!animate) return
      if (elementRef.current === null || !appear) return

      const timeline = gsap.timeline({
        delay,
        scrollTrigger: hasScrollTrigger
          ? {
              trigger: elementRef.current,
              start: 'top 66.667%',
              toggleActions: 'play none none none',
            }
          : undefined,
      })
      const lines = elementRef.current.querySelectorAll('.UIIcon')
      if (lines.length > 0) {
        timeline.fromTo(
          lines,
          {
            yPercent: 115,
          },
          {
            yPercent: 0,
            stagger: 0.05,
            duration: 0.7,
            ease: 'quart.out',
          },
          0
        )
      }
    })

    return () => ctx.revert()
  }, [delay, appear, elementRef, gsap, hasScrollTrigger])

  return (
    <Component
      ref={elementRef}
      className={`${className ? className : ''} py-[0.2em] transform-gpu`}
      aria-label={translatedText}
    >
      {translatedText.split('\n').map((line, indexLine) => (
        <div aria-hidden key={indexLine} className='Line__Parent overflow-hidden py-[0.2em] -my-[0.4em]'>
          <div className='Line'>
            {hasSideDecoration}
            <span className='mx-16'>
              {line
                .trim()
                .split('')
                .map((char, indexChar) => (
                  <span key={indexChar} className='Char inline-block relative'>
                    {indexChar % 2 === 1 ? (
                      <>
                        <span className='Char__Inner inline-block relative'>{char === ' ' ? <>&nbsp;</> : char}</span>
                      </>
                    ) : char === ' ' ? (
                      <>&nbsp;</>
                    ) : (
                      char
                    )}
                  </span>
                ))}
            </span>
            {hasSideDecoration}
          </div>
        </div>
      ))}
    </Component>
  )
}
